//codex-- Extend Favorite Buttons with an independent per-employee scheduler preference.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUserData } from '../context/UserData';
import { SCHEDULER_DEPARTMENTS, getFullSchedulerDefault, saveFullSchedulerDefault } from '../utils/fullSchedulerSettings';

//codex-- Keep the existing settings style and ignore responses from an obsolete account or mount.
export default function FullSchedulerConfigCp() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const employeeId = useUserData()?.users?.id;
  const navigate = useNavigate();
  const requestVersion = useRef(0);
  const [department, setDepartment] = useState('Field');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  //codex-- Report session failures through the existing login route.
  const showError = useCallback((failure, message) => {
    if (failure.response?.status === 401) navigate('/login');
    else setError(message);
  }, [navigate]);

  //codex-- Reload intentionally replaces unsaved edits with the server's current setting.
  const loadPreference = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError('');
    if (!employeeId) return;
    try {
      const selected = await getFullSchedulerDefault(apiUrl);
      if (version === requestVersion.current) setDepartment(selected);
    } catch (failure) {
      if (version === requestVersion.current) showError(failure, 'Could not load your default department. Please try again.');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [apiUrl, employeeId, showError]);

  //codex-- Reset when the authenticated employee changes and invalidate all pending work on cleanup.
  useEffect(() => {
    setDepartment('Field');
    setSaving(false);
    loadPreference();
    return () => { requestVersion.current += 1; };
  }, [loadPreference]);

  //codex-- A failed save preserves the unsaved selection; only a successful response shows confirmation.
  const savePreference = async (event) => {
    event.preventDefault();
    if (saving || loading || !employeeId) return;
    const version = ++requestVersion.current;
    setSaving(true);
    setError('');
    try {
      const saved = await saveFullSchedulerDefault(apiUrl, department);
      if (version === requestVersion.current) {
        setDepartment(saved);
        toast.success('Default department saved.', { position: 'top-center' });
      }
    } catch (failure) {
      if (version === requestVersion.current) showError(failure, 'Could not save your default department. Please try again.');
    } finally {
      if (version === requestVersion.current) setSaving(false);
    }
  };

  //codex-- Match the client's compact indigo settings controls without altering favorite buttons.
  return (
    <section className="mt-8 border-t border-gray-200 pt-6" aria-labelledby="full-scheduler-settings-title">
      <h2 id="full-scheduler-settings-title" className="text-base font-semibold text-gray-900">Full-Scheduler</h2>
      <p className="mt-1 text-sm text-gray-500">Choose the department selected when you open the full scheduler.</p>
      {error && (
        <div className="mt-3" role="alert">
          <p className="text-sm text-red-600">{error}</p>
          <button type="button" onClick={loadPreference} disabled={loading || saving} className="mt-1 text-sm font-semibold text-indigo-600">
            Reload setting
          </button>
        </div>
      )}
      <form onSubmit={savePreference} className="mt-4" aria-busy={loading || saving}>
        <label htmlFor="full-scheduler-default-department" className="block text-sm font-medium text-gray-700">Default Department</label>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <select id="full-scheduler-default-department" value={department}
            onChange={(event) => setDepartment(event.target.value)} disabled={loading || saving}
            className="w-44 rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
            <option value="">All Depts</option>
            {SCHEDULER_DEPARTMENTS.map((dept) => <option key={dept} value={dept}>{dept}</option>)}
          </select>
          <button type="submit" disabled={loading || saving} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">
            {loading ? 'Loading...' : saving ? 'Saving...' : 'Save Default'}
          </button>
        </div>
      </form>
    </section>
  );
}
